import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Bell, Shield, LogOut, LayoutGrid, FolderSearch, CalendarDays, CalendarClock, Youtube, ShoppingBag, GraduationCap, MessageCircle, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/components/CommunityManagerChat";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";

export const YOUTUBE_CHANNEL = "https://www.youtube.com/@helpsolvethecase";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Navbar() {
  const { user, logout } = useAuth();
  const chat = useChat();
  const navigate = useNavigate();
  const [notif, setNotif] = useState({ notifications: [], unread: 0 });

  useEffect(() => {
    if (!user) return;
    const fetchNotif = () => api.get("/me/notifications").then(({ data }) => setNotif(data)).catch(() => {});
    fetchNotif();
    const id = setInterval(fetchNotif, 45000);
    const onFocus = () => fetchNotif();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user]);

  const markRead = async () => {
    await api.post("/notifications/read").catch(() => {});
    setNotif((n) => ({ ...n, unread: 0 }));
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-charcoal/90 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="nav-home-link">
          <Logo size="sm" />
          <span className="hidden sm:block font-display font-semibold tracking-tight text-sm text-white/90">
            Help Solve The Case<sup className="text-[9px] align-super ml-0.5">™</sup>
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {user ? (
            <>
              <Link to="/cases" data-testid="nav-cases-link" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                <FolderSearch className="w-4 h-4" /> Cases
              </Link>
              {user.academy_ok && (
                <Link to="/academy" data-testid="nav-academy-link" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                  <GraduationCap className="w-4 h-4" /> Academy
                </Link>
              )}
              <Link to="/shop" data-testid="nav-shop-link" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                <ShoppingBag className="w-4 h-4" /> Shop
              </Link>
              <Link to="/calendar" data-testid="nav-calendar-link" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                <CalendarDays className="w-4 h-4" /> Calendar
              </Link>
              <Link to="/dashboard" data-testid="nav-dashboard-link" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                <LayoutGrid className="w-4 h-4" /> Dashboard
              </Link>
              <button onClick={() => chat?.openChat()} data-testid="nav-community-manager-btn" title="Chat with the Community Manager"
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                <MessageCircle className="w-4 h-4" /> <span className="hidden lg:inline">Community Manager</span>
              </button>
              <a href={YOUTUBE_CHANNEL} target="_blank" rel="noreferrer" data-testid="nav-youtube-link" title="Watch on YouTube"
                className="hidden sm:flex items-center p-2 text-white/70 hover:text-red-500 transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
              {user.role === "admin" && (
                <Link to="/members" data-testid="nav-members-link" className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                  <Users className="w-4 h-4" /> <span className="hidden sm:inline">Members</span>
                </Link>
              )}
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-link" className="flex items-center gap-2 px-3 py-2 text-sm text-gold hover:text-gold-hover transition-colors">
                  <Shield className="w-4 h-4" /> <span className="hidden sm:inline">Command</span>
                </Link>
              )}

              <DropdownMenu onOpenChange={(o) => o && markRead()}>
                <DropdownMenuTrigger asChild>
                  <button className="relative p-2 text-white/70 hover:text-gold transition-colors" data-testid="nav-notifications-btn">
                    <Bell className="w-5 h-5" />
                    {notif.unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-charcoal text-[10px] font-bold flex items-center justify-center">
                        {notif.unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 bg-surface border-white/10 text-white">
                  <DropdownMenuLabel className="label-mono">Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {notif.notifications.length === 0 && <div className="px-2 py-6 text-center text-sm text-white/40">No notifications yet</div>}
                  {notif.notifications.slice(0, 8).map((n, i) => {
                    const cls = "block px-2 py-2 text-sm text-white/80 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors";
                    if (n.link && /^https?:\/\//.test(n.link))
                      return <a key={n.notif_id || i} href={n.link} target="_blank" rel="noreferrer" className={cls} data-testid={`notif-${i}`}>{n.message}</a>;
                    if (n.link)
                      return <Link key={n.notif_id || i} to={n.link} className={cls} data-testid={`notif-${i}`}>{n.message}</Link>;
                    return <div key={n.notif_id || i} className={cls} data-testid={`notif-${i}`}>{n.message}</div>;
                  })}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-2" data-testid="nav-user-menu">
                    <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold text-sm font-semibold font-mono">
                      {(user.name || user.email)[0]?.toUpperCase()}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-surface border-white/10 text-white">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user.name}</div>
                    <div className="text-xs text-white/40 font-mono">{user.email}</div>
                    <div className="mt-1 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-gold/15 text-gold">
                      {user.role === "admin" ? "Admin" : user.subscription_status === "active" ? "Founding Member" : "Free"}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/dashboard")}>Dashboard</DropdownMenuItem>
                  {(user.subscription_status === "active" || user.role === "admin") && (
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/library")} data-testid="nav-library-link">Video Library</DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate(`/u/${user.user_id}`)} data-testid="nav-profile-link">My profile</DropdownMenuItem>
                  {user.subscription_status !== "active" && user.role !== "admin" && (
                    <DropdownMenuItem className="cursor-pointer text-gold" onClick={() => navigate("/pricing")}>Become a Member</DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer text-red-400" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <a href={YOUTUBE_CHANNEL} target="_blank" rel="noreferrer" data-testid="nav-youtube-link-guest" title="Watch on YouTube"
                className="flex items-center p-2 text-white/70 hover:text-red-500 transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
              <Link to="/shop" data-testid="nav-shop-link-guest" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                <ShoppingBag className="w-4 h-4" /> Shop
              </Link>
              <Link to="/events" data-testid="nav-events-link-guest" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                <CalendarClock className="w-4 h-4" /> Events
              </Link>
              <Link to="/login" data-testid="nav-login-link" className="px-4 py-2 text-sm text-white/80 hover:text-gold transition-colors">Sign in</Link>
              <Link to="/pricing" data-testid="nav-join-link" className="px-4 py-2 text-sm font-semibold bg-gold text-charcoal rounded-sm hover:bg-gold-hover transition-colors">Join</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export default Navbar;
